import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Button, ButtonGroup, Alert, Spinner } from 'react-bootstrap';
import api from '../api/axios';
import { logout } from '../store/authSlice';
import { loadSession, clearExam } from '../store/examSlice';
import GroupCodeModal from './GroupCodeModal';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [hasIncomplete, setHasIncomplete] = useState(false);
  const [incompleteSession, setIncompleteSession] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [modalGroup, setModalGroup] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // Fetch groups from backend
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await api.get('/groups.php');
        setGroups(res.data);
      } catch (err) {
        console.error('Failed to load groups', err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  // Only show groups that have questions
  const visibleGroups = groups.filter(group => group.has_questions);

  // Auto-select first visible group when they load
  useEffect(() => {
    if (visibleGroups.length > 0 && !selectedGroup) {
      setSelectedGroup(visibleGroups[0].group_name);
    }
  }, [visibleGroups, selectedGroup]);

  // Check for incomplete exam session
  useEffect(() => {
    const checkIncomplete = async () => {
      try {
        const res = await api.get('/exam.php?action=incomplete');
        if (res.data.length > 0) {
          setHasIncomplete(true);
          setIncompleteSession(res.data[0]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    checkIncomplete();
  }, []);

  const handleContinue = async () => {
    if (!incompleteSession) return;
    try {
      const res = await api.get(`/exam.php?action=get&sessionId=${incompleteSession.id}`);
      let questionsUrl = `/questions.php?limit=${res.data.total_questions}`;
      if (res.data.group_name === 'C') {
        questionsUrl += '&order=id';
      }
      const questionsRes = await api.get(questionsUrl);
      dispatch(loadSession({
        sessionId: res.data.id,
        mode: res.data.mode,
        currentIndex: res.data.current_question_index,
        answers: res.data.answers || {},
        questions: questionsRes.data,
        completed: res.data.completed,
        score: res.data.score,
      }));
      navigate('/exam');
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleModeClick = (mode) => {
    if (!selectedGroup) {
      alert('Please select an exam set first.');
      return;
    }
    const group = groups.find(g => g.group_name === selectedGroup);
    if (!group?.has_questions) {
      alert('This exam set has no questions yet. Please choose another set.');
      return;
    }
    const requiredCode = mode === 'tutor' ? group?.tutor_code : group?.exam_code;
    if (!requiredCode) {
      if (mode === 'tutor') {
        navigate('/tutor/setup', { state: { group: selectedGroup } });
      } else {
        dispatch(clearExam());
        navigate('/exam', { state: { group: selectedGroup } });
      }
    } else {
      setModalGroup(selectedGroup);
      setPendingMode(mode);
    }
  };

  const handleCodeSuccess = () => {
    const actualGroup = modalGroup;
    if (pendingMode === 'tutor') {
      navigate('/tutor/setup', { state: { group: actualGroup } });
    } else {
      dispatch(clearExam());
      navigate('/exam', { state: { group: actualGroup } });
    }
    setModalGroup(null);
    setPendingMode(null);
  };

  const handleModalClose = () => {
    setModalGroup(null);
    setPendingMode(null);
  };

  if (loadingGroups) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p>Loading exam sets...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-5 text-center">
      <h1 className="mb-4">Welcome, {user?.username}!</h1>

      {/* Exam Set Selection – Grid of Buttons (only visible groups) */}
      {visibleGroups.length > 0 && (
        <div className="mb-4">
          <h5>Select an exam set:</h5>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 120px))',
            gap: '10px',
            justifyContent: 'center',
            marginTop: '15px'
          }}>
            {visibleGroups.map((group) => (
              <Button
                key={group.group_name}
                variant={selectedGroup === group.group_name ? 'primary' : 'outline-primary'}
                onClick={() => setSelectedGroup(group.group_name)}
                style={{ width: '100%' }}
              >
                {group.group_name === 'D' ? 'HIGH YIELD' : group.group_name}
              </Button>
            ))}
          </div>
        </div>
      )}

      <ButtonGroup className="mt-3">
        <Button variant="primary" size="lg" onClick={() => handleModeClick('tutor')}>
          Tutor Mode
        </Button>
        <Button variant="outline-primary" size="lg" onClick={() => handleModeClick('exam')}>
          Exam Mode
        </Button>
        <Button variant="success" size="lg" onClick={() => navigate('/case-studies')}>
          Case Studies
        </Button>
        <Button variant="secondary" size="lg" onClick={() => navigate('/exam/history')}>
          My Exam History
        </Button>
        {hasIncomplete && (
          <Button variant="primary" size="lg" onClick={handleContinue}>
            Continue Last Session
          </Button>
        )}
        {user?.is_admin && (
          <>
            <Button variant="info" size="lg" onClick={() => navigate('/admin/approvals')}>
              Approve Users
            </Button>
            <Button variant="info" size="lg" onClick={() => navigate('/admin/add-user')}>
              Add User
            </Button>
            <Button variant="info" size="lg" onClick={() => navigate('/admin/questions')}>
              Manage Questions
            </Button>
            <Button variant="info" size="lg" onClick={() => navigate('/admin/bulk-add-users')}>
              Bulk Add Users
            </Button>
          </>
        )}
        <Button variant="outline-primary" size="lg" onClick={handleLogout}>
          Logout
        </Button>
      </ButtonGroup>

      {!hasIncomplete && (
        <Alert variant="info" className="mt-4">
          No incomplete exam sessions. Start a new exam!
        </Alert>
      )}

      <GroupCodeModal
        show={modalGroup !== null}
        groupName={modalGroup}
        mode={pendingMode}
        onSuccess={handleCodeSuccess}
        onClose={handleModalClose}
      />
    </Container>
  );
};

export default Dashboard;